import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Search, ChevronRight, Loader2, X, Navigation } from 'lucide-react';
import { getNearestDistrictsApi, searchDistrictsApi, updateUserDistrictApi, type District } from '@/lib/api';
import './DistrictSelector.css';

interface Props {
  userLat?: number;
  userLng?: number;
  currentDistrict?: string | null;
  userId?: string | null;
  isOpen: boolean;
  onClose: () => void;
  onDistrictSelected: (district: District, updatedUser?: any) => void;
}

export default function DistrictSelector({
  userLat,
  userLng,
  currentDistrict,
  userId,
  isOpen,
  onClose,
  onDistrictSelected,
}: Props) {
  const [query, setQuery] = useState('');
  const [nearestDistricts, setNearestDistricts] = useState<District[]>([]);
  const [searchResults, setSearchResults] = useState<District[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchCacheRef = useRef<Map<string, District[]>>(new Map());

  useEffect(() => {
    if (!isOpen) return;

    // 1. Instant Render from Local Browser Cache (0ms latency!)
    if (typeof window !== 'undefined' && userLat && userLng) {
      const cacheKey = `kheldrop_nearest_districts_${userLat.toFixed(2)}_${userLng.toFixed(2)}`;
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setNearestDistricts(parsed);
          }
        }
      } catch {}
    }

    // 2. Background Revalidation (Stale-While-Revalidate)
    if (userLat && userLng) {
      if (nearestDistricts.length === 0) setLoading(true);
      getNearestDistrictsApi(userLat, userLng, 5)
        .then((d) => {
          if (d && d.length > 0) {
            setNearestDistricts(d);
            try {
              const cacheKey = `kheldrop_nearest_districts_${userLat.toFixed(2)}_${userLng.toFixed(2)}`;
              sessionStorage.setItem(cacheKey, JSON.stringify(d));
            } catch {}
          }
        })
        .finally(() => setLoading(false));
    }

    setTimeout(() => inputRef.current?.focus(), 120);
  }, [isOpen, userLat, userLng]);

  const handleSearch = useCallback((val: string) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = val.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      return;
    }

    // Check memory cache first
    const cached = searchCacheRef.current.get(trimmed.toLowerCase());
    if (cached) {
      setSearchResults(cached);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const results = await searchDistrictsApi(trimmed, 10);
      searchCacheRef.current.set(trimmed.toLowerCase(), results);
      setSearchResults(results);
    }, 180);
  }, []);

  // OPTIMISTIC 0ms SELECTION: Local Browser Cache First, Background DB Sync
  const handleSelect = useCallback((district: District) => {
    // 1. Instant 0ms UI update: Close modal & notify parent immediately!
    onDistrictSelected(district);
    onClose();

    // 2. Persist in local browser cache immediately
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('kheldrop_selected_district', JSON.stringify(district));
        localStorage.setItem('kheldrop_live_coords', JSON.stringify({ lat: district.lat, lng: district.lng }));
        localStorage.setItem('kheldrop_location_permission', 'granted');

        const cachedUserStr = localStorage.getItem('kheldrop_auth_user');
        if (cachedUserStr) {
          const userObj = JSON.parse(cachedUserStr);
          userObj.district = district.name;
          userObj.districtId = district.id;
          userObj.districtSlug = district.slug;
          userObj.state = district.state;
          localStorage.setItem('kheldrop_auth_user', JSON.stringify(userObj));
        }
      } catch (err) {
        console.warn('Local cache write error:', err);
      }
    }

    // 3. Background Async Transfer to Database (Non-blocking, fire-and-forget)
    updateUserDistrictApi({
      id: district.id,
      slug: district.slug,
      name: district.name,
      state: district.state,
      state_code: district.state_code,
    })
      .then((res) => {
        if (res?.user && typeof window !== 'undefined') {
          try {
            localStorage.setItem('kheldrop_auth_user', JSON.stringify(res.user));
          } catch {}
        }
      })
      .catch((e) => {
        console.warn('Background district sync completed with local fallback:', e);
      });
  }, [onDistrictSelected, onClose]);

  if (!isOpen) return null;

  const displayList = query.trim().length >= 2 ? searchResults : nearestDistricts;
  const sectionLabel = query.trim().length >= 2 ? 'Search Results' : 'Nearest Districts';

  return (
    <div className="ds-backdrop" role="dialog" aria-modal="true" aria-label="Select your district">
      <div className="ds-panel">
        <div className="ds-header">
          <div className="ds-header-icon">
            <MapPin size={18} strokeWidth={2} />
          </div>
          <div className="ds-header-text">
            <h2 className="ds-title">Select Your District</h2>
            <p className="ds-subtitle">
              {currentDistrict ? `Current: ${currentDistrict}` : 'Choose the district you play in'}
            </p>
          </div>
          <button className="ds-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="ds-search-wrap">
          <Search size={16} className="ds-search-icon" />
          <input
            ref={inputRef}
            className="ds-search-input"
            type="text"
            placeholder="Search district or state…"
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {query && (
            <button className="ds-search-clear" onClick={() => handleSearch('')} aria-label="Clear">
              <X size={14} />
            </button>
          )}
        </div>

        <div className="ds-list-label">
          {loading ? (
            <span className="ds-loading-row">
              <Loader2 size={14} className="ds-spinner" />
              Locating nearest districts…
            </span>
          ) : (
            <>
              {(userLat && userLng && query.trim().length < 2) && (
                <span className="ds-label-icon">
                  <Navigation size={12} />
                </span>
              )}
              {sectionLabel}
            </>
          )}
        </div>

        <ul className="ds-list">
          {displayList.length === 0 && !loading && (
            <li className="ds-empty">
              {query.trim().length >= 2 ? 'No districts found' : 'Enable location for suggestions'}
            </li>
          )}
          {displayList.map((d) => (
            <li key={d.slug}>
              <button
                className="ds-item"
                onClick={() => handleSelect(d)}
              >
                <span className="ds-item-dot" />
                <span className="ds-item-info">
                  <span className="ds-item-name">{d.name}</span>
                  <span className="ds-item-state">
                    {d.state} &bull; <span className="ds-uid-tag">UID: {userId ? `${userId.slice(-6)}+${d.id}` : `USR+${d.id}`}</span>
                  </span>
                </span>
                {d.distance_km !== undefined && (
                  <span className="ds-item-dist">{d.distance_km.toFixed(0)} km</span>
                )}
                <ChevronRight size={14} className="ds-item-arrow" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
